'use client';

import type { UIMessage } from 'ai';
import { CopyIcon, RefreshCcwIcon } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Action, Actions } from './elements/actions';
import { Message, MessageContent } from './elements/message';
import { Reasoning, ReasoningContent, ReasoningTrigger } from './elements/reasoning';
import { Response } from './elements/response';
import { Tool, ToolContent, ToolHeader, ToolInput, ToolOutput } from './elements/tool';
import { MessageSources } from './message-sources';
import { ToolWidgetRenderer } from './message/tool-widget-renderer';
import { hasWidget } from './message/widget-registry';

interface MessageListProps {
  messages: UIMessage[];
  status: string;
  onRegenerate: () => void;
}

export function MessageList({ messages, status, onRegenerate }: MessageListProps) {
  const { t } = useTranslation('chat2');
  return (
    <>
      {messages.map((message, messageIndex) => {
        const widgets: Array<{ toolName: string; result: any; args: any }> = [];
        message.parts?.forEach((part) => {
          if (
            part.type.startsWith('tool-') &&
            'state' in part &&
            'input' in part &&
            part.state === 'output-available' &&
            'output' in part
          ) {
            const toolName = part.type.replace('tool-', '');
            if (hasWidget(toolName)) {
              widgets.push({ toolName, result: part.output, args: part.input });
            }
          }
        });
        return (
          <div key={message.id} className={messageIndex > 0 ? 'mt-6' : ''}>
            <MessageSources message={message} />
            {message.parts?.map((part, i) => {
              switch (part.type) {
                case 'text':
                  return (
                    <Fragment key={`${message.id}-${i}`}>
                      <Message from={message.role}>
                        <MessageContent>
                          <Response>{part.text}</Response>
                        </MessageContent>
                      </Message>
                      {message.role === 'assistant' && i === message.parts!.length - 1 && (
                        <Actions className="mt-2">
                          <Action onClick={() => onRegenerate()} label={t('common:actions.retry')}>
                            <RefreshCcwIcon className="size-3" />
                          </Action>
                          <Action
                            onClick={() => navigator.clipboard.writeText(part.text || '')}
                            label={t('common:actions.copy')}
                          >
                            <CopyIcon className="size-3" />
                          </Action>
                        </Actions>
                      )}
                    </Fragment>
                  );
                case 'reasoning':
                  return (
                    <Reasoning
                      key={`${message.id}-${i}`}
                      className="w-full"
                      isStreaming={
                        status === 'streaming' &&
                        i === message.parts!.length - 1 &&
                        message.id === messages.at(-1)?.id
                      }
                    >
                      <ReasoningTrigger />
                      <ReasoningContent>{part.text}</ReasoningContent>
                    </Reasoning>
                  );
                default:
                  if (part.type.startsWith('tool-')) {
                    if ('state' in part && 'input' in part) {
                      const toolName = part.type.replace('tool-', '');
                      if (hasWidget(toolName) && part.state === 'output-available') {
                        return null;
                      }
                      const shouldAutoOpen =
                        part.state === 'output-available' || part.state === 'output-error';
                      return (
                        <Tool
                          key={`${message.id}-${i}`}
                          defaultOpen={shouldAutoOpen}
                          className={i > 0 ? 'mt-4' : ''}
                        >
                          <ToolHeader type={part.type as `tool-${string}`} state={part.state} />
                          <ToolContent>
                            <ToolInput input={part.input} />
                            <ToolOutput
                              output={part.output}
                              errorText={'errorText' in part ? part.errorText : undefined}
                              toolName={toolName}
                              args={part.input}
                            />
                          </ToolContent>
                        </Tool>
                      );
                    }
                  }
                  return null;
              }
            })}
            {widgets.length > 0 && (
              <div className="mt-4 space-y-4">
                {widgets.map((widget, widgetIndex) => (
                  <ToolWidgetRenderer
                    key={`${message.id}-widget-${widgetIndex}`}
                    toolName={widget.toolName}
                    result={widget.result}
                    args={widget.args}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
